import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useFigureCompositionStore } from '@/store/figure-composition';
import { usePlotsStore } from '@/store/plots';
import { Loader2, Download, FileDown, Archive } from 'lucide-react';
import { Separator } from '@/components/ui/separator';

interface ExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ExportDialog({ open, onOpenChange }: ExportDialogProps) {
  const currentComposition = useFigureCompositionStore((state) => state.currentComposition);
  const exportToSVG = useFigureCompositionStore((state) => state.exportToSVG);
  const exportToPNG = useFigureCompositionStore((state) => state.exportToPNG);
  const exportPanelToSVG = useFigureCompositionStore((state) => state.exportPanelToSVG);
  const exportPanelToPNG = useFigureCompositionStore((state) => state.exportPanelToPNG);
  const exportPanelsToZip = useFigureCompositionStore((state) => state.exportPanelsToZip);
  const plots = usePlotsStore((state) => state.plots);

  const [fileName, setFileName] = useState(currentComposition?.title || 'figure');
  const [pngDpi, setPngDpi] = useState(300);
  const [isExporting, setIsExporting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  const panelsWithPlots = currentComposition?.panels.filter(p => p.plotId) || [];
  const loadPlots = usePlotsStore((state) => state.loadPlots);
  
  // Helper to get plot title for a panel
  const getPlotTitle = (plotId: string | undefined): string | undefined => {
    if (!plotId) return undefined;
    const plot = plots.find(p => p.id === plotId);
    return plot?.title;
  };

  // Load plots when dialog opens and composition is available
  useEffect(() => {
    if (open && currentComposition?.projectId && plots.length === 0) {
      loadPlots(currentComposition.projectId).catch(console.error);
    }
  }, [open, currentComposition?.projectId, plots.length, loadPlots]);

  useEffect(() => {
    if (currentComposition?.title) {
      setFileName(currentComposition.title);
    }
  }, [currentComposition?.title]);

  useEffect(() => {
    // Clear error when dialog opens/closes
    if (open) {
      setErrorMessage(null);
    }
  }, [open]);

  const handleDpiChange = (value: string) => {
    const num = Number.parseInt(value, 10);
    if (Number.isNaN(num) || num < 72 || num > 600) {
      return;
    }
    setPngDpi(num);
  };

  const handleExportSVG = async () => {
    if (!currentComposition) return;

    setIsExporting(true);
    setErrorMessage(null);
    try {
      const svg = await exportToSVG();
      const blob = new Blob([svg], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileName || 'figure'}.svg`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to export SVG';
      console.error('Failed to export SVG:', error);
      setErrorMessage(message);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportPNG = async () => {
    if (!currentComposition) return;

    setIsExporting(true);
    setErrorMessage(null);
    try {
      const blob = await exportToPNG(pngDpi);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileName || 'figure'}.png`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to export PNG';
      console.error('Failed to export PNG:', error);
      setErrorMessage(message);
    } finally {
      setIsExporting(false);
    }
  };

  const sanitizeFileName = (name: string) => {
    // Extract figure number if present (e.g., "Figure 1" -> "Fig 1")
    const figMatch = name.match(/figure\s*(\d+)/i);
    if (figMatch) {
      return `Fig ${figMatch[1]}`;
    }
    // Otherwise sanitize and use as-is
    return name.replace(/[^a-zA-Z0-9\s]/g, '').trim() || 'Fig';
  };

  // Extract just the letter from panel labels like "(a)" -> "a" or "a" -> "a"
  const extractPanelLetter = (label: string | undefined, index: number): string => {
    if (!label) {
      return String.fromCharCode(97 + index); // 'a', 'b', 'c', etc.
    }
    // Extract letter from formats like "(a)", "a", "(A)", "A", etc.
    const match = label.match(/\(?([a-z])\)?/i);
    if (match) {
      return match[1].toLowerCase();
    }
    // If no match, use first character if it's a letter
    const firstChar = label.trim()[0];
    if (firstChar && /[a-z]/i.test(firstChar)) {
      return firstChar.toLowerCase();
    }
    // Fallback to index-based letter
    return String.fromCharCode(97 + index);
  };

  // Sanitize plot title for use in filename
  const sanitizePlotTitle = (title: string): string => {
    return title
      .replace(/[^a-zA-Z0-9\s_-]/g, '') // Remove special chars except spaces, dashes, underscores
      .replace(/\s+/g, ' ') // Normalize whitespace
      .trim()
      .substring(0, 50); // Limit length
  };

  const handleExportPanel = async (panelId: string, format: 'svg' | 'png') => {
    if (!currentComposition) return;

    setIsExporting(true);
    setErrorMessage(null);
    try {
      const panel = currentComposition.panels.find(p => p.id === panelId);
      if (!panel) {
        throw new Error(`Panel ${panelId} not found`);
      }

      const panelIndex = panelsWithPlots.findIndex(p => p.id === panelId);
      const panelLetter = extractPanelLetter(panel.label, panelIndex);
      const plotTitle = getPlotTitle(panel.plotId);
      
      // Build filename: format is "figure(a) Plot Title.png"
      const baseName = sanitizeFileName(fileName || 'figure');
      let downloadName: string;
      if (plotTitle) {
        const sanitizedTitle = sanitizePlotTitle(plotTitle);
        downloadName = `${baseName}(${panelLetter}) ${sanitizedTitle}.${format}`;
      } else {
        downloadName = `${baseName}(${panelLetter}).${format}`;
      }
      
      console.log(`[handleExportPanel] Exporting panel ${panelId} (${panelLetter}) as ${format}`);
      
      let blob: Blob;
      if (format === 'svg') {
        const svg = await exportPanelToSVG(panelId);
        if (!svg || svg.length === 0) {
          throw new Error('Empty SVG content');
        }
        blob = new Blob([svg], { type: 'image/svg+xml' });
      } else {
        blob = await exportPanelToPNG(panelId, pngDpi);
        if (!blob || blob.size === 0) {
          throw new Error('Empty PNG blob');
        }
      }

      console.log(`[handleExportPanel] Downloading as: ${downloadName}`);
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = downloadName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      const message = error instanceof Error ? error.message : `Failed to export panel ${format.toUpperCase()}`;
      console.error(`Failed to export panel ${format.toUpperCase()}:`, error);
      setErrorMessage(message);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportZip = async (format: 'svg' | 'png') => {
    if (!currentComposition) return;

    setIsExporting(true);
    setErrorMessage(null);
    try {
      const blob = await exportPanelsToZip(format, pngDpi);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const baseName = sanitizeFileName(fileName || 'figure');
      a.download = `${baseName}_panels.${format}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      const message = error instanceof Error ? error.message : `Failed to export ZIP`;
      console.error('Failed to export ZIP:', error);
      setErrorMessage(message);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Export Figure</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="fileName">File name</Label>
            <Input
              id="fileName"
              value={fileName}
              onChange={(e) => setFileName(e.target.value)}
              placeholder="figure"
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="pngDpi">PNG DPI</Label>
            <Input
              id="pngDpi"
              type="number"
              min="72"
              max="600"
              value={pngDpi}
              onChange={(e) => handleDpiChange(e.target.value)}
            />
          </div>
          {errorMessage && (
            <Alert variant="destructive">
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}
          
          {/* Individual Panels Section */}
          {panelsWithPlots.length > 0 && (
            <>
              <Separator className="my-2" />
              <div className="space-y-3">
                <div>
                  <Label className="text-sm font-semibold">Individual Panels</Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Download each panel as a separate file (recommended for journal submissions)
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleExportZip('svg')}
                    disabled={isExporting || !currentComposition}
                    className="w-full"
                  >
                    {isExporting ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Archive className="mr-2 h-4 w-4" />
                    )}
                    All Panels (SVG ZIP)
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleExportZip('png')}
                    disabled={isExporting || !currentComposition}
                    className="w-full"
                  >
                    {isExporting ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Archive className="mr-2 h-4 w-4" />
                    )}
                    All Panels (PNG ZIP)
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
        <DialogFooter className="!grid !grid-cols-2 !gap-0">
          <Button onClick={handleExportSVG} disabled={isExporting || !currentComposition} className="w-full" size="sm">
            {isExporting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                Download SVG
              </>
            )}
          </Button>
          <Button onClick={handleExportPNG} disabled={isExporting || !currentComposition} className="w-full" size="sm">
            {isExporting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                Download PNG
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

